import PropTypes from 'prop-types';
import { useRef, useState, useCallback } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
// components
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const REPLY_ID_RE = /^\[#msg-([^\]]+)\]\s*$/;
const EMOJI_FONT_FAMILY = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

function safeSliceEmoji(value, length) {
  return Array.from(String(value || '')).slice(0, length).join('');
}

function getDisplayBody(body) {
  if (!body) return '';
  const lines = String(body).split('\n');
  let i = 0;

  if (lines[i] && REPLY_ID_RE.test(lines[i])) {
    i += 1;
  }

  while (i < lines.length && lines[i]?.startsWith('>')) {
    i += 1;
  }

  if (i > 0 && lines[i] === '') {
    i += 1;
  }

  return lines.slice(i).join('\n') || String(body);
}

export default function ChatMessageInput({
  recipients,
  onSendCompose,
  onSendMessage,
  currentConversationId,
  replyingTo,
  onClearReply,
  participants = [],
}) {
  const fileRef = useRef(null);
  const imageRef = useRef(null);

  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);

  const replyAuthorName = (() => {
    if (!replyingTo) return '';
    const sender = participants.find((p) => String(p.id) === String(replyingTo.senderId));
    return sender?.name || sender?.firstName || sender?.email || 'message';
  })();

  const buildReplyPayload = () => {
    if (!replyingTo) return null;

    const originalBody = getDisplayBody(replyingTo.body || replyingTo.content || '');

    return {
      id: replyingTo.id,
      senderId: replyingTo.senderId,
      authorName: replyAuthorName,
      body: originalBody,
      snippet: String(originalBody).slice(0, 200).replace(/\n/g, ' '),
      contentType: replyingTo.contentType || replyingTo.messageType || 'text',
    };
  };

  const handleAttach = useCallback(() => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  }, []);

  const handleImageAttach = useCallback(() => {
    if (imageRef.current) {
      imageRef.current.click();
    }
  }, []);

  const handleChange = useCallback((event) => {
    setMessage(event.target.value);
  }, []);

  const handleSend = useCallback(
    async (event) => {
      if (event.key !== 'Enter') return;
      if (!message || isSending) return;
      const replyTo = buildReplyPayload();
      try {
        setIsSending(true);
        if (currentConversationId) {
          await onSendMessage(message, 'text', null, { replyTo });
        } else {
          await onSendCompose(message);
        }
        setMessage('');
        if (onClearReply) onClearReply();
      } catch (err) {
        console.error('Failed to send message', err);
      } finally {
        setIsSending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentConversationId, message, isSending, onSendCompose, onSendMessage, onClearReply, replyingTo]
  );

  const handleFileChange = useCallback(
    async (event, isImage = false) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Check if we have a conversation to send to
      if (!currentConversationId && !recipients.length) {
        console.error('No conversation selected');
        return;
      }

      setIsUploading(true);

      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);

        // Upload file to server
        const response = await axios.post(API_ENDPOINTS.messages.upload, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.success) {
          const { url } = response.data.data;
          const messageType = isImage || file.type.startsWith('image/') ? 'image' : 'file';

          // Send the message with the uploaded file URL
          if (currentConversationId) {
            onSendMessage(url, messageType, url);
          } else {
            onSendCompose(url);
          }
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        setIsUploading(false);
        // Reset the file input
        event.target.value = '';
      }
    },
    [currentConversationId, recipients, onSendMessage, onSendCompose]
  );

  return (
    <>
      {replyingTo && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: (theme) => `solid 1px ${theme.palette.divider}`,
            bgcolor: 'background.neutral',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Iconify icon="solar:reply-bold" width={16} sx={{ color: 'primary.main', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'primary.main' }}>
              Replying to {replyAuthorName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {safeSliceEmoji(getDisplayBody(replyingTo.body || ''), 200)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClearReply}>
            <Iconify icon="eva:close-fill" width={14} />
          </IconButton>
        </Box>
      )}
      <InputBase
        value={message}
        onKeyUp={handleSend}
        onChange={handleChange}
        placeholder={isSending ? 'Sending…' : 'Type a message'}
        disabled={(!recipients.length && !currentConversationId) || isUploading || isSending}
        startAdornment={
          <IconButton disabled={isUploading || isSending} onClick={(e) => setEmojiAnchor(e.currentTarget)}>
            <Iconify icon="eva:smiling-face-fill" />
          </IconButton>
        }
        endAdornment={
          <Stack direction="row" sx={{ flexShrink: 0 }} alignItems="center">
            {isSending && <CircularProgress size={18} sx={{ mr: 1 }} />}
            {isUploading ? (
              <CircularProgress size={24} sx={{ m: 1 }} />
            ) : (
              <>
                <IconButton
                  onClick={handleImageAttach}
                  disabled={!recipients.length && !currentConversationId}
                >
                  <Iconify icon="solar:gallery-add-bold" />
                </IconButton>
                <IconButton
                  onClick={handleAttach}
                  disabled={!recipients.length && !currentConversationId}
                >
                  <Iconify icon="eva:attach-2-fill" />
                </IconButton>
                <IconButton disabled>
                  <Iconify icon="solar:microphone-bold" />
                </IconButton>
              </>
            )}
          </Stack>
        }
        sx={{
          px: 1,
          height: 56,
          flexShrink: 0,
          fontFamily: EMOJI_FONT_FAMILY,
          borderTop: (theme) => `solid 1px ${theme.palette.divider}`,
        }}
      />

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Picker
          data={data}
          onEmojiSelect={(emoji) => {
            setMessage((prev) => prev + emoji.native);
            setEmojiAnchor(null);
          }}
          theme="light"
        />
      </Popover>

      {/* Hidden file input for images */}
      <input
        type="file"
        ref={imageRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => handleFileChange(e, true)}
      />

      {/* Hidden file input for all files */}
      <input
        type="file"
        ref={fileRef}
        accept="image/*,.pdf,.doc,.docx,.txt"
        style={{ display: 'none' }}
        onChange={(e) => handleFileChange(e, false)}
      />
    </>
  );
}

ChatMessageInput.propTypes = {
  currentConversationId: PropTypes.string,
  onSendCompose: PropTypes.func,
  onSendMessage: PropTypes.func,
  recipients: PropTypes.array,
  replyingTo: PropTypes.object,
  onClearReply: PropTypes.func,
  participants: PropTypes.array,
};
